import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CloudUpload,
  Search,
  CheckCircle,
  Cancel,
  Warning,
  VerifiedUser,
  Block
} from '@mui/icons-material';
import { fingerprintService } from '../services/fingerprintService';
import { FingerprintResult } from '../types';

const VerifyFingerprint: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FingerprintResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleVerify = async () => {
    if (!file || !clientId.trim()) {
      setError('Por favor selecciona un archivo e ingresa un ID de cliente');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult(null);

      const verifyResult = await fingerprintService.verifyFingerprint(file, clientId);
      setResult(verifyResult);
    } catch (error: any) {
      setError(error.message || 'Error al verificar el fingerprint');
    } finally {
      setLoading(false);
    }
  };

  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'dcm':
        return 'DICOM';
      case 'json':
        return 'JSON';
      default:
        return 'Binario';
    }
  };

  const getVerificationStatus = (result: FingerprintResult) => {
    const matches = [result.tag_matches, result.lsb_matches, result.trailing_matches];
    const matchCount = matches.filter(Boolean).length;
    
    if (matchCount >= 2) {
      return { status: 'success', icon: <VerifiedUser />, text: 'Archivo de Cliente Detectado' };
    } else if (matchCount === 1) {
      return { status: 'warning', icon: <Warning />, text: 'Posible Archivo de Cliente' };
    } else {
      return { status: 'error', icon: <Block />, text: 'No es Archivo de Cliente' };
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
        Verificar Fingerprint
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ flex: { xs: 1, md: 1 } }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Seleccionar Archivo
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <input
                accept=".dcm,.json,application/octet-stream"
                style={{ display: 'none' }}
                id="verify-file-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="verify-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{ width: '100%', py: 2 }}
                >
                  Seleccionar Archivo
                </Button>
              </label>
            </Box>

            {file && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Archivo seleccionado:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {file.name}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      label={getFileType(file.name)} 
                      size="small" 
                      color="primary" 
                    />
                    <Chip 
                      label={`${(file.size / 1024 / 1024).toFixed(2)} MB`} 
                      size="small" 
                      variant="outlined" 
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            <TextField
              fullWidth
              label="ID del Cliente"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Ej: cliente123"
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleVerify}
              disabled={!file || !clientId.trim() || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
              sx={{
                py: 1.5,
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8, #6a4190)'
                }
              }}
            >
              {loading ? 'Verificando...' : 'Verificar Fingerprint'}
            </Button>
          </Paper>
        </Box>

        <Box sx={{ flex: { xs: 1, md: 1 } }}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Resultado de Verificación
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Card variant="outlined">
                <CardContent>
                  {(() => {
                    const status = getVerificationStatus(result);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: `${status.status}.main`, mr: 1 }}>
                          {status.icon}
                        </Box>
                        <Typography variant="h6" color={`${status.status}.main`}>
                          {status.text}
                        </Typography>
                      </Box>
                    );
                  })()}

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Fingerprint esperado:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      backgroundColor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      wordBreak: 'break-all',
                      mb: 2
                    }}
                  >
                    {result.expected_fingerprint}
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom>
                    Resultados por técnica:
                  </Typography>

                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        {result.tag_matches ? <CheckCircle color="success" /> : <Cancel color="error" />}
                      </ListItemIcon>
                      <ListItemText 
                        primary="Tag Privado DICOM" 
                        secondary={result.tag_matches ? 'Coincide' : 'No coincide'}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        {result.lsb_matches ? <CheckCircle color="success" /> : <Cancel color="error" />}
                      </ListItemIcon>
                      <ListItemText 
                        primary="Watermark en Píxeles (LSB)" 
                        secondary={result.lsb_matches ? 'Coincide' : 'No coincide'}
                      />
                    </ListItem>
                    
                    <ListItem>
                      <ListItemIcon>
                        {result.trailing_matches ? <CheckCircle color="success" /> : <Cancel color="error" />}
                      </ListItemIcon>
                      <ListItemText 
                        primary="Trailing Marker Binario" 
                        secondary={result.trailing_matches ? 'Coincide' : 'No coincide'}
                      />
                    </ListItem>
                  </List>

                  {result.errors.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Errores durante la verificación:
                      </Typography>
                      {result.errors.map((error, index) => (
                        <Typography key={index} variant="body2">
                          • {error}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

            {!result && !error && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Search sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Selecciona un archivo y un ID de cliente para verificar
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default VerifyFingerprint;
